import { ApiResponse, PagedResults } from "../../interfaces/genericResponse/GenericResponse";
import { AllEvent, AllEventTeam, AllTeams, AllUsers, InternalInvitationResponseAction, InternalInvitationResponseReq, EventData, GetAllTeamsReqDto, GetTeamDto, userTask } from "../../interfaces/identity/calendar/Events";
import Team from "../../interfaces/identity/Team";
import {
    CalendarAttendeeMode,
    CalendarDirectoryApiResponse,
    CalendarDirectoryOptions,
} from "../../interfaces/calendar/calendarDirectoryOptions";
import { apiGet, apiPost } from "../../utilities/api-client/apiRequest";
import { buildQuery } from "../../utilities/common/buildQueryParams";
import { normalizeLeaveUtcValue } from "../../utilities/leaveDateTime";


export interface CalendarTimeOff {
    id: number;
    userId: string;
    userName?: string;
    userColor?: string;
    startTime: string;
    endTime: string;
    leaveType: string;
    requestType: string;
    durationMinutes: number;
    comment?: string | null;
    status: "PENDING" | "APPROVED" | "REJECTED" | string;
    isCancellationPending: boolean;
    canDelete: boolean;
}

export interface CalendarHoliday {
    id: number;
    title: string;
    description?: string | null;
    startDate: string;
    endDate: string;
}

export interface CalendarFeed {
    events: AllEvent[];
    timeOff: CalendarTimeOff[];
    holidays: CalendarHoliday[];
}

const normalizeCalendarTimeOff = (leave: CalendarTimeOff): CalendarTimeOff => ({
    ...leave,
    startTime: normalizeLeaveUtcValue(leave.startTime),
    endTime: normalizeLeaveUtcValue(leave.endTime),
});

const normalizeApprovedLeaveResponse = (leave: any): any => ({
    ...leave,
    startDate: leave.startDate ? normalizeLeaveUtcValue(leave.startDate) : leave.startDate,
    endDate: leave.endDate ? normalizeLeaveUtcValue(leave.endDate) : leave.endDate,
});

export async function getAllEvents(
    authToken: string,
    start: Date,
    end: Date,
    targetUserId?: string,
    signal?: AbortSignal,
    options?: { includeEvents?: boolean; includeTimeOff?: boolean; includeManagedEvents?: boolean; calendarStartDate?: string; calendarEndDateExclusive?: string }
): Promise<CalendarFeed | null> {
    const query = new URLSearchParams({
        startTime: start.toISOString(),
        endTime: end.toISOString(),
    });

    // The backend already supports an optional `id` query parameter.
    // Supplying it allows a manager to load a person's calendar directly
    // without requiring a Department/Team selection first.
    if (targetUserId) {
        query.set("id", targetUserId);
    }
    if (options?.includeEvents === false) query.set("includeEvents", "false");
    if (options?.includeTimeOff === false) query.set("includeTimeOff", "false");
    // Only the unfiltered main calendar opts into the server-derived reporting
    // hierarchy (or the owner's organization). Personal callers stay personal.
    if (options?.includeManagedEvents === true && !targetUserId) query.set("includeManagedEvents", "true");
    if (options?.calendarStartDate) query.set("calendarStartDate", options.calendarStartDate);
    if (options?.calendarEndDateExclusive) query.set("calendarEndDateExclusive", options.calendarEndDateExclusive);

    const apiUrl =
        `${process.env.REACT_APP_API_BASE_URL}/CalendarEvent/GetAllEvent?${query.toString()}`;

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            signal,
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            console.error("Failed to fetch calendar feed:", response.statusText);
            return null;
        }

        const json = await response.json();
        const payload = json.data;

        // Temporary compatibility with an older API build that returned only the event array.
        if (Array.isArray(payload)) {
            return {
                events: payload,
                timeOff: [],
                holidays: payload.flatMap((event: any) => event.holidays || []),
            };
        }

        return {
            events: payload?.events ?? [],
            // Time-off is persisted as UTC, but legacy API/database paths can
            // return an offset-less ISO value. Normalize once at the transport
            // boundary so presentation components cannot reinterpret it as a
            // local wall-clock value.
            timeOff: (payload?.timeOff ?? []).map(normalizeCalendarTimeOff),
            holidays: payload?.holidays ?? [],
        };
    } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") throw err;
        console.error("Error fetching calendar feed:", err);
        return null;
    }
}


export async function getAllEventsWithFilter(
    authToken: string
): Promise<AllEvent[] | null> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/CalendarEvent/GetAllEvent`;

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            console.error("Failed to fetch events:", response.statusText);
            return null;
        }

        const json = await response.json();
        const data: AllEvent[] = json.data ?? [];

        return data;
    } catch (err) {
        console.error("Error fetching roles:", err);
        return null;
    }
}

export async function getCalendarEventById(
    eventId: string,
    authToken: string,
): Promise<ApiResponse<AllEvent>> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/CalendarEvent/GetEventbyId/${encodeURIComponent(eventId)}`;
    const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
    });

    const result = await response.json();
    if (!response.ok) {
        throw new Error(result?.message || `Failed to load meeting (${response.status}).`);
    }

    return result;
}

export async function getAllEmployees(
    authToken: string
): Promise<AllUsers[] | null> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/Authentication/get-combined-user-list`;

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });


        if (!response.ok) {
            console.error("Failed to fetch events:", response.statusText);
            return null;
        }

        const json = await response.json();
        const data: AllUsers[] = json.data ?? [];

        return data;
    } catch (err) {
        console.error("Error fetching roles:", err);
        return null;
    }
}


export async function getAllTeam(
    authToken: string,
    payload: GetAllTeamsReqDto
): Promise<ApiResponse<PagedResults<AllTeams>>> {
    const queryString = buildQuery(payload);
    const apiUrl: string = `/TeamandRegion/GetTeamDetails?${queryString}`;

    var res = apiGet<ApiResponse<PagedResults<AllTeams>>>(
        apiUrl, authToken
    );

    return res;
}


function mapCalendarDirectoryOptions(payload: CalendarDirectoryApiResponse | null | undefined): CalendarDirectoryOptions {
    return {
        teams: payload?.teams ?? [],
        people: (payload?.people ?? []).map((person) => ({
            userId: person.id,
            userName: person.name,
            email: person.email,
            type: person.type,
            roles: Array.isArray(person.roles) ? person.roles : [],
            teamIds: Array.isArray(person.teamIds) ? person.teamIds : [],
        })),
        appliedScopes: payload?.appliedScopes ?? [],
        isLegacyMode: payload?.isLegacyMode ?? false,
    };
}

async function getCalendarDirectoryOptions(
    apiUrl: string,
    authToken: string,
): Promise<CalendarDirectoryOptions> {
    const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Failed to load calendar options (${response.status}).`);
    }

    const json = await response.json();
    return mapCalendarDirectoryOptions(json?.data);
}

/**
 * Teams and people the current user may view in Calendar.
 * Uses the backend calendar:view AuthV2 permission and falls back to legacy
 * behavior server-side for users who are not enrolled in AuthV2.
 */
export async function getCalendarVisibilityOptions(
    authToken: string,
): Promise<CalendarDirectoryOptions> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/CalendarEvent/visibility-options`;
    return getCalendarDirectoryOptions(apiUrl, authToken);
}

/**
 * Teams and people available while creating or editing a calendar event.
 * Edit mode requires the event ID because calendar:edit is record-scoped.
 */
export async function getCalendarAttendeeOptions(
    authToken: string,
    mode: CalendarAttendeeMode = "create",
    eventId?: string,
): Promise<CalendarDirectoryOptions> {
    const query = new URLSearchParams({ mode });

    if (mode === "edit") {
        if (!eventId) {
            throw new Error("eventId is required when loading edit attendee options.");
        }
        query.set("eventId", eventId);
    }

    const apiUrl =
        `${process.env.REACT_APP_API_BASE_URL}/CalendarEvent/attendee-options?${query.toString()}`;

    return getCalendarDirectoryOptions(apiUrl, authToken);
}


export async function getMyTeams(
    authToken: string,
): Promise<Team[]> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/users/teams`;

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        const json = await response.json();

        return json;
    } catch (err) {
        throw err;
    }
}

export async function getMyParticipantsAndCoworkers(
    authToken: string,
): Promise<AllUsers[]> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/users/participantAndCoworkers`;

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        const json = await response.json();

        const allusers = json.map((user: any) => {
            return {
                userId: user.id,
                userName: user.name,
                email: user.email,
                type: user.type,
                roles: Array.isArray(user.roles) ? user.roles : [],
            };
        })

        return allusers;
    } catch (err) {
        throw err;
    }
}

export async function getAllTeamByRegions(
    authToken: string,
    regionIds: number[]
): Promise<GetTeamDto[]> {
    const query = regionIds.map(id => `regionIds=${id}`).join("&");
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/TeamandRegion/GetTeamsByRegions?${query}`;

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        const json = await response.json();

        return json;
    } catch (err) {
        throw err;
    }
}

export async function getAllTemaEvents(
    authToken: string,
    teamId: number,
    signal?: AbortSignal,
    start?: Date,
    end?: Date,
    userIds?: string[]
): Promise<AllEventTeam | null> {
    const query = new URLSearchParams({
        teamId: teamId.toString(),
        includeTimeOff: "false",
    });
    if (start) query.set("startTime", start.toISOString());
    if (end) query.set("endTime", end.toISOString());
    if (userIds?.length) query.set("userIds", userIds.join(","));
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/manager/team/calendar?${query.toString()}`;

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            signal,
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) return null;
        const json = await response.json();
        return json.data ?? null;
    } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") throw err;
        console.error("Error fetching team events:", err);
        return null;
    }
}

// 2. Update Internal Accept/Reject route (Moved to CalendarInvitationController)
export async function AcceptRejectMeetingInvitationAsync(
    calendarEventId: string,
    action: InternalInvitationResponseAction,
    authToken: string,
): Promise<ApiResponse<boolean>> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/invitation/respond`;

    const payload: InternalInvitationResponseReq = {
        calendarEventId: calendarEventId,
        action,
    }

    try {
        const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
        })

        return await response.json();
    } catch (err) {
        throw err;
    }
}

// 3. NEW: Add the External RSVP method for Guest Users (No Auth Token required)
// Expects token-based format with token and action (ACCEPT or DECLINE)
export async function RespondToExternalInviteAsync(
    token: string,
    action: 'ACCEPT' | 'DECLINE'
): Promise<ApiResponse<boolean>> {
    // This hits the [AllowAnonymous] endpoint
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/public/invitation/respond`;

    const payload = {
        token: token,
        action: action
    };

    try {
        const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
        });

        return await response.json();
    } catch (err) {
        throw err;
    }
}

export async function createEvent(
    eventData: EventData,
    authToken: string
): Promise<ApiResponse<any>> {
    const apiUrl: string = `/CalendarEvent/CreateEvent`;

    var res = apiPost<ApiResponse<any>>(
        apiUrl, eventData, authToken
    )

    return res;
    // try {
    //     const response = await fetch(apiUrl, {
    //         method: "POST",
    //         headers: {
    //             Authorization: `Bearer ${authToken}`,
    //             "Content-Type": "application/json",
    //         },
    //         body: JSON.stringify(eventData),
    //     });

    //     if (response.status === 409) {
    //         const errorJson = await response.json();
    //         return { success: false, statusCode: 409, message: errorJson.message || "Conflict: Event overlaps with an existing event." };
    //     }

    //     const json = await response.json();
    //     return json;
    // } catch (err) {
    //     console.error("Error creating event:", err);
    //     return null;
    // }
}

export async function createWeeklySupervisionMeeting(
    instance: number | undefined,
    eventData: EventData,
    authToken: string
): Promise<any | null> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/reporting/weekly-supervision/weekly-supervision/${instance}/schedule-meeting`;

    try {
        const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(eventData),
        });

        // if (!response.ok) {
        //   const error = await response.text();
        //   console.error("Failed to create event:", error);
        //   return null;
        // }

        const json = await response.json();
        return json;
    } catch (err) {
        console.error("Error creating event:", err);
        return null;
    }
}

export async function updateEvent(
    eventData: EventData & { id: string },
    authToken: string,
    updateScope?: 'single' | 'future' // Add updateScope parameter
): Promise<any | null> {
    let apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/CalendarEvent/UpdateEventById/${eventData.id}`;

    if (updateScope) {
        apiUrl += `?updateScope=${updateScope}`;
    }

    try {
        const response = await fetch(apiUrl, {
            method: "PUT",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(eventData),
        });

        if (response.status === 409) {
            const errorJson = await response.json();
            return { success: false, statusCode: 409, message: errorJson.message || "Conflict: Event overlaps with an existing event." };
        }

        const json = await response.json();
        return json;
    } catch (err) {
        console.error("Error updating event:", err);
        return null;
    }
}

export async function deleteEvent(eventId: string, authToken: string, deleteScope?: 'single' | 'future'): Promise<boolean> {
    let apiUrl = `${process.env.REACT_APP_API_BASE_URL}/CalendarEvent/DeleteEvent/${eventId}`;

    if (deleteScope) {
        apiUrl += `?deleteScope=${deleteScope}`;
    }

    try {
        const response = await fetch(apiUrl, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${authToken}`,
            },
        });

        if (!response.ok) {
            const error = await response.text();
            console.error("Failed to delete event:", error);
            return false;
        }

        return true;
    } catch (err) {
        console.error("Error deleting event:", err);
        return false;
    }
}

export async function getAllUserTask(
    authToken: string
): Promise<userTask[] | null> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/CalendarEvent/userassignedtask`;

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            console.error("Failed to fetch tasks:", response.statusText);
            return null;
        }

        const json = await response.json();

        if (!json.success || !Array.isArray(json.data)) {
            console.warn("Unexpected API response format:", json);
            return null;
        }

        return json.data as userTask[];
    } catch (err) {
        console.error("Error fetching tasks:", err);
        return null;
    }
}

export async function getAllLeaves(
    userIds: string[], 
    authToken: string,
    start: Date,
    end: Date,
    signal?: AbortSignal
): Promise<any[] | null> {
    const startTime = start.toISOString();
    const endTime = end.toISOString();

    // Join the array into a comma-separated string (e.g., "userA,userB,userC")
    const idsQuery = userIds.join(',');

    // Update the URL parameter to match the controller
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/CalendarEvent/GetAllLeaves?userIds=${idsQuery}&startTime=${startTime}&endTime=${endTime}`;

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            signal,
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        })

        if (!response.ok) {
            console.error("Failed to get the leave data:", response.statusText)
            return null;
        }
        const json = await response.json();

        return (json.data ?? []).map(normalizeApprovedLeaveResponse);
    } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") throw err;
        console.error("Service level:", err)
        return null;
    }
}

export async function getEventDatesByUserIdAsync(
    UserId: string,
    authToken: string
): Promise<ApiResponse<string[]>> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/CalendarEvent/upcomming/${UserId}/getCalendarEventDates`;

    try {
        const response = await fetch(apiUrl, {
            method: "Get",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        })

        return await response.json();
    } catch (err) {
        throw err;
    }
}

export async function deleteTimeOffRequest(timeOffId: number, authToken: string): Promise<any> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/TimeOff/delete-timeOff/${timeOffId}`;

    try {
        const response = await fetch(apiUrl, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${authToken}`,
            },
        });

        const json = await response.json();
        return json;
    } catch (err) {
        console.error("Error deleting time off:", err);
        return { success: false, message: "A network error occurred." };
    }
}
